import { Dialog, Link, Stack } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import AppleAppStore from 'assets/apple-app-store.svg?react';
import GooglePlayStore from 'assets/google-play-store.svg?react';
import { Capacitor } from '@capacitor/core';
import { CSSProperties } from 'react';
import { MAP_ENTITY_DRAWER_WIDTH } from 'features/mapping/components/MapEntityDrawer';
import { useMapEntityDrawerState } from 'features/mapping/hooks/useMapEntityDrawerState';
import { getMapDialogCommonStyles } from 'features/mapping/mapping.utils';
import {
  AppStoreLinks,
  MAP_DOM_ELEMENT_ID,
  SEARCH_DRAWER_WIDTH,
} from '../../../../../constants';

const useStyles = makeStyles<{
  isDrawerEntitySelected: boolean;
  isSearchDrawerOpen: boolean;
}>()((theme, { isDrawerEntitySelected, isSearchDrawerOpen }) => {
  const mapDialogCommonStyles = getMapDialogCommonStyles(theme, {
    isDrawerEntitySelected,
    isSearchDrawerOpen,
    minWidth: 0,
    minSnapPct: 0,
  });

  const desktopOffset = isSearchDrawerOpen
    ? MAP_ENTITY_DRAWER_WIDTH - SEARCH_DRAWER_WIDTH
    : MAP_ENTITY_DRAWER_WIDTH;

  return {
    root: {
      ...mapDialogCommonStyles.root,
      // Center the app store dialog over the map viewport when the drawer is open
      // (Mirrors the logic used for laptops in mapDialogCommonStyles)
      [theme.breakpoints.between('tablet', 'laptop')]: {
        transform: isDrawerEntitySelected
          ? `translateX(-${desktopOffset / 2}px)`
          : 'translateX(0)',
        transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
      },
      [theme.breakpoints.down('tablet')]: {
        ...(mapDialogCommonStyles.root[
          theme.breakpoints.down('tablet')
        ] as CSSProperties),
        transform: 'none',
        transition: 'none',
      },
    },
    paper: {
      ...mapDialogCommonStyles.paper,
      width: 'fit-content !important',
      minWidth: 'unset',
      bottom: Capacitor.isNativePlatform()
        ? 'calc(30px + env(safe-area-inset-bottom))'
        : 16,
      [theme.breakpoints.down('tablet')]: {
        bottom: 40,
      },
    },
  };
});

export const AppStoresDialog = (): JSX.Element => {
  const { open: isSearchDrawerOpen } = useSearchDrawerState();
  const { open: isDrawerEntitySelected } = useMapEntityDrawerState();
  const { classes } = useStyles({ isDrawerEntitySelected, isSearchDrawerOpen });

  return (
    <Dialog
      open
      classes={{
        root: classes.root,
        paper: classes.paper,
      }}
      hideBackdrop
      // Prevent the dialog from stealing focus on open. Without these props MUI
      // moves focus into the dialog's first interactive element, which causes the
      // parent page to scroll when the app is embedded in an iframe.
      disableAutoFocus
      disableEnforceFocus
      container={(): HTMLElement | null =>
        document.getElementById(MAP_DOM_ELEMENT_ID)
      }
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ width: '100%', justifyContent: 'center', p: 2 }}
      >
        <Link
          href={AppStoreLinks.ios}
          target="_blank"
          sx={{ pointerEvents: 'auto', height: '32px' }}
        >
          <AppleAppStore width="108px" height="32px" />
        </Link>
        <Link
          href={AppStoreLinks.android}
          target="_blank"
          sx={{ pointerEvents: 'auto', height: '32px' }}
        >
          <GooglePlayStore width="108px" height="32px" />
        </Link>
      </Stack>
    </Dialog>
  );
};
